import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';
import { getFAQContent } from '@/data/faqContent';
import { 
  Layers, 
  Sun, 
  Moon, 
  ChevronDown,
  Rocket,
  CreditCard,
  Coins,
  Sparkles,
  UserCog,
  ArrowLeft
} from 'lucide-react';

const FAQPage = () => {
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Get translated FAQ content
  const faqContent = getFAQContent(t, language);

  const faqCategories = [
    {
      title: t('faq.gettingStarted'),
      icon: <Rocket className="w-5 h-5" />,
      questions: faqContent.gettingStarted
    },
    {
      title: t('faq.billing'),
      icon: <CreditCard className="w-5 h-5" />,
      questions: faqContent.billing
    },
    {
      title: t('faq.creditsUsage'),
      icon: <Coins className="w-5 h-5" />,
      questions: faqContent.credits
    },
    {
      title: t('faq.features'),
      icon: <Sparkles className="w-5 h-5" />,
      questions: faqContent.features
    },
    {
      title: t('faq.accountCancellation'),
      icon: <UserCog className="w-5 h-5" />,
      questions: faqContent.account
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="faq-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-background" />
            </div>
            <span className="font-semibold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </Link>
          
          <div className="flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Link to="/auth">
              <Button>{t('nav.getStarted')}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">{t('nav.backToHome')}</span>
        </Link>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('faq.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-accent rounded-xl flex items-center justify-center">
                  {category.icon}
                </div>
                <h2 
                  className="text-xl font-semibold"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {category.title}
                </h2>
              </div>

              {/* Questions */}
              <div className="space-y-2">
                {category.questions.map((item, itemIndex) => {
                  const key = `${categoryIndex}-${itemIndex}`;
                  const isOpen = openItems[key];

                  return (
                    <div 
                      key={itemIndex}
                      className="border border-border rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => toggleItem(categoryIndex, itemIndex)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                        data-testid={`faq-${categoryIndex}-${itemIndex}`}
                      >
                        <span className="font-medium pr-4">{item.q}</span>
                        <ChevronDown 
                          className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="px-5 pb-4 text-muted-foreground">
                              {item.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Still have questions? */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center p-8 bg-muted/30 rounded-2xl"
        >
          <h3 
            className="text-xl font-semibold mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('faq.stillHaveQuestions')}
          </h3>
          <p className="text-muted-foreground mb-4">
            {t('faq.hereToHelp')}
          </p>
          <Link to="/contact">
            <Button data-testid="contact-from-faq">{t('faq.contactSupport')}</Button>
          </Link>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-foreground rounded flex items-center justify-center">
              <Layers className="w-4 h-4 text-background" />
            </div>
            <span className="font-medium">MockupPro</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/pricing" className="hover:text-foreground">{t('nav.pricing')}</Link>
            <Link to="/contact" className="hover:text-foreground">{t('nav.contact')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
